import { RobotAgent } from "../RobotAgent";
import { Index3D, MeshBlock, parseNvbloxMeshBuffer } from "./mesh-visualizer-reader";
import { RTVisualizer } from "./RTVisualizer";
import * as THREE from 'three';

export interface MeshBlockObj {
  mesh: THREE.Mesh;
  block: MeshBlock;
  index: { x: number; y: number; z: number };
}

export class MeshVisualizer extends RTVisualizer {
  // three dimemsional indexer for blocks
  public blocks: Map<string, MeshBlockObj> = new Map();
  meshGroup: THREE.Group<THREE.Object3DEventMap>;
  public easyMaterial = new THREE.MeshStandardMaterial({
    color: 0x808080,
    side: THREE.DoubleSide,
    metalness: 0.5,
    roughness: 0.5,
  })

  constructor(agent: RobotAgent) {
    super(agent);
    this.meshGroup = new THREE.Group();
    this.meshGroup.position.set(0, 0, 0.5);
    this.meshGroup.name = 'nvbloxMeshGroup';
    agent.visual.obj.parent!.add(this.meshGroup);
  }

  public setActive(active: boolean) {
    super.setActive(active);
    this.meshGroup.visible = active;
    if (!active) {
      this.meshGroup.clear();
      this.blocks.clear();
    }
  }

  public updateMesh(data: ArrayBuffer) {
    if (!this.active) {
      return;
    }
    const mesh = parseNvbloxMeshBuffer(data);
    console.log(`mesh size: ${mesh.blockSize}, blocks: ${mesh.blocks.length}`);
    // mesh.blockSize = 0.02;
    // let maxIndex = { x: -Infinity, y: -Infinity, z: -Infinity };
    for (let i = 0; i < mesh.blockIndices.length; i++) {
      const index = mesh.blockIndices[i];
      const block = mesh.blocks[i];
      const key = `${index.x},${index.y},${index.z}`;
      let blockObj = this.blocks.get(key);
      if (blockObj) {
        this.meshGroup.remove(blockObj.mesh);
        blockObj.mesh.geometry.dispose();
        (blockObj.mesh.material as THREE.Material).dispose();
        this.blocks.delete(key);
      }
      if (block.triangles.length > 0) {
        const obj = this.buildMeshFromBlock(block, index, mesh.blockSize);
        if (obj) {
          this.meshGroup.add(obj.mesh);
          this.blocks.set(key, obj);
        }
      }
    }
  }

  private buildMeshFromBlock(block: MeshBlock, index: Index3D, blockSize: number): MeshBlockObj | null {
    if (block.triangles.length === 0 || block.vertices.length === 0) {
      return null;
    }

    // Flatten vertices into Float32Array (x, y, z per vertex)
    const vertexCount = block.vertices.length;
    const positions = new Float32Array(vertexCount * 3);
    for (let i = 0; i < vertexCount; i++) {
      const v = block.vertices[i];
      positions[i * 3 + 0] = v.x;
      positions[i * 3 + 1] = v.y;
      positions[i * 3 + 2] = v.z;
    }

    // Flatten normals if available
    // let normals: Float32Array | undefined;
    // if (block.normals.length === vertexCount) {
    //   normals = new Float32Array(vertexCount * 3);
    //   for (let i = 0; i < vertexCount; i++) {
    //     const n = block.normals[i];
    //     normals[i * 3 + 0] = n.x;
    //     normals[i * 3 + 1] = n.y;
    //     normals[i * 3 + 2] = n.z;
    //   }
    // }

    // Flatten colors if available (RGBA, but Three.js uses RGB or RGBA depending on material)
    let colors: Float32Array | undefined;
    if (block.colors.length === vertexCount) {
      colors = new Float32Array(vertexCount * 3); // We'll use RGB for MeshLambertMaterial / MeshPhongMaterial
      for (let i = 0; i < vertexCount; i++) {
        const c = block.colors[i];
        colors[i * 3 + 0] = c.r;
        colors[i * 3 + 1] = c.g;
        colors[i * 3 + 2] = c.b;
        // Alpha is ignored in most standard materials unless using MeshBasicMaterial with vertexColors
      }
    }

    // Triangle indices (must be Uint16 or Uint32 depending on size)
    const triangleCount = block.triangles.length;
    const indices = triangleCount > 65535
      ? new Uint32Array(block.triangles)
      : new Uint16Array(block.triangles);

    // Create BufferGeometry
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    // if (normals) {
    //   geometry.setAttribute('normal', new THREE.BufferAttribute(normals, 3));
    // }
    if (colors) {
      geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    }
    geometry.setIndex(new THREE.BufferAttribute(indices, 1));

    // Compute normals if not provided (fallback)
    // if (!normals) {
    geometry.computeVertexNormals();
    // }

    // Bounding box for frustum culling, etc.
    geometry.computeBoundingBox();
    geometry.computeBoundingSphere();

    // Material: use vertex colors if available, otherwise a default colored material
    const material = this.easyMaterial;

    const mesh = new THREE.Mesh(geometry, material);

    // Position the block in world space
    // nvblox block origin is at (index.x * blockSize, index.y * blockSize, index.z * blockSize)
    // mesh.position.set(
    //   index.x * blockSize,
    //   index.y * blockSize,
    //   index.z * blockSize
    // );

    // Optional: give it a name for debugging
    mesh.name = `nvblox_block_${index.x}_${index.y}_${index.z}`;

    return {
      mesh,
      block,
      index,
    };
  }
}